import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import Spinner from './Spinner';

const MapContainer = styled.div`
  height: 500px;
  width: 100%;
  margin: 2rem 0;
  border-radius: 12px;
  overflow: hidden;
  position: relative;
  border: 2px solid #0d9488;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
`;

const MapLegend = styled.div`
  position: absolute;
  bottom: 20px;
  right: 20px;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 10px 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 10;
  
  &::before {
    content: "";
    width: 10px;
    height: 10px;
    background-color: #0d9488;
    border-radius: 50%;
    display: inline-block;
  }
`;

const ErrorMessage = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
  color: #666;
  padding: 20px;
  text-align: center;
  
  h3 {
    margin-bottom: 10px;
    color: #d32f2f;
  }
  
  p {
    max-width: 400px;
  }
`;

const LoadingContainer = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: rgba(0, 0, 0, 0.05);
`;

export interface Marker {
  id: number;
  position: {
    lat: number;
    lng: number;
  };
  title: string;
  icon?: string;
}

interface InteractiveMapProps {
  markers: Marker[];
  center?: {
    lat: number;
    lng: number;
  };
  zoom?: number;
  legendText: string;
  apiKey?: string;
}

declare global {
  interface Window {
    initMap: () => void;
    google: any;
    googleMapCallback?: () => void;
  }
}

const InteractiveMap: React.FC<InteractiveMapProps> = ({
  markers,
  center,
  zoom = 10,
  legendText,
  apiKey = 'AIzaSyAitC9Vvf6QR41wnS1ge3uoTGj4uMTTVHY'
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [map, setMap] = useState<any>(null);
  const [googleMarkers, setGoogleMarkers] = useState<any[]>([]);
  const [infoWindow, setInfoWindow] = useState<any>(null);
  const [mapError, setMapError] = useState<string | null>(null);
  const callbackName = 'googleMapCallback';

  // Calculate the map center if not specified
  const mapCenter = center || (markers.length > 0 
    ? {
        lat: markers.reduce((sum, marker) => sum + marker.position.lat, 0) / markers.length,
        lng: markers.reduce((sum, marker) => sum + marker.position.lng, 0) / markers.length
      }
    : { lat: 43.7228, lng: 10.4017 }); // Default to Pisa

  // Load the Google Maps script with error handling
  useEffect(() => {
    // Define the callback function
    window[callbackName] = () => {
      setMapLoaded(true);
    };

    // Check if script already exists
    const existingScript = document.getElementById('google-maps-script');
    if (!existingScript) {
      const script = document.createElement('script');
      script.id = 'google-maps-script';
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=${callbackName}`;
      script.async = true;
      script.defer = true;
      script.onerror = () => {
        setMapError('Failed to load Google Maps. Please check your internet connection and try again.');
      };
      
      document.head.appendChild(script);
    } else if (window.google && window.google.maps) {
      setMapLoaded(true);
    }
    
    // Cleanup function
    return () => {
      window[callbackName] = undefined;
    };
  }, [apiKey, callbackName]);

  // Initialize the map when the script is loaded
  useEffect(() => {
    if (mapLoaded && mapRef.current && !map && window.google) {
      try {
        // Create map with custom styling
        const mapOptions = {
          center: mapCenter,
          zoom: zoom,
          fullscreenControl: true,
          streetViewControl: false,
          mapTypeControl: true,
          zoomControl: true,
          styles: [
            {
              "featureType": "water",
              "elementType": "geometry",
              "stylers": [{"color": "#e9e9e9"}, {"lightness": 17}]
            },
            {
              "featureType": "landscape",
              "elementType": "geometry",
              "stylers": [{"color": "#f5f5f5"}, {"lightness": 20}]
            },
            {
              "featureType": "road.highway",
              "elementType": "geometry.fill",
              "stylers": [{"color": "#ffffff"}, {"lightness": 17}]
            },
            {
              "featureType": "poi",
              "elementType": "geometry",
              "stylers": [{"color": "#f5f5f5"}, {"lightness": 21}]
            }
          ]
        };
        
        const newMap = new window.google.maps.Map(mapRef.current, mapOptions);
        setMap(newMap);
        
        // Initialize InfoWindow with custom styling
        const infoWindowOptions = {
          pixelOffset: new window.google.maps.Size(0, -35),
          maxWidth: 300,
        };
        setInfoWindow(new window.google.maps.InfoWindow(infoWindowOptions));
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
        setMapError('Failed to initialize the map. Please refresh the page and try again.');
      }
    }
  }, [mapLoaded, mapRef, map, mapCenter, zoom]);

  // Handle markers when the map is available
  useEffect(() => {
    if (map && infoWindow) {
      try {
        // Remove existing markers
        googleMarkers.forEach(marker => marker.setMap(null));
        
        // Add new markers
        const newMarkers = markers.map(markerData => {
          // Create custom icon for accommodations
          const isMainMarker = markerData.id === 0; // The main city marker
          
          const icon = {
            url: markerData.icon || (isMainMarker 
              ? 'https://maps.google.com/mapfiles/ms/icons/blue-dot.png'
              : 'https://maps.google.com/mapfiles/ms/icons/red-dot.png'),
            scaledSize: new window.google.maps.Size(isMainMarker ? 42 : 30, isMainMarker ? 42 : 30),
            origin: new window.google.maps.Point(0, 0),
            anchor: new window.google.maps.Point(isMainMarker ? 21 : 15, isMainMarker ? 42 : 30)
          };
          
          // Create marker
          const marker = new window.google.maps.Marker({
            position: markerData.position,
            map: map,
            title: markerData.title,
            icon: icon,
            animation: window.google.maps.Animation.DROP,
            optimized: true,
            zIndex: isMainMarker ? 1 : 10 // Main city marker should be below other markers
          });
          
          // Custom info window content
          const infoContent = `
            <div style="padding: 10px; max-width: 280px;">
              <h3 style="margin: 0 0 8px; color: #0d9488; font-size: 16px; border-bottom: 1px solid #eee; padding-bottom: 5px;">${markerData.title}</h3>
              <p style="margin: 0; font-size: 13px; color: #666;">${isMainMarker ? 'Main point of interest' : 'Accommodation location'}</p>
            </div>
          `;
          
          // Add click event to show info window
          marker.addListener('click', () => {
            infoWindow.setContent(infoContent);
            infoWindow.open(map, marker);
          });
          
          return marker;
        });
        
        setGoogleMarkers(newMarkers);
      } catch (error) {
        console.error('Error adding markers:', error);
        setMapError('Failed to add location markers to the map.');
      }
    }
  }, [map, markers, infoWindow]);

  // Update map center and zoom when they change
  useEffect(() => {
    if (map && mapCenter) {
      map.setCenter(mapCenter);
      map.setZoom(zoom);
    }
  }, [map, mapCenter, zoom]);

  if (mapError) {
    return (
      <MapContainer>
        <ErrorMessage>
          <h3>Map Error</h3>
          <p>{mapError}</p>
        </ErrorMessage>
      </MapContainer>
    );
  }

  return (
    <MapContainer>
      {!mapLoaded ? (
        <LoadingContainer>
          <Spinner />
        </LoadingContainer>
      ) : (
        <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
      )}
      <MapLegend>{legendText}</MapLegend>
    </MapContainer>
  );
};

export default InteractiveMap; 